import { useId } from 'react';
import { svgId } from '@/lib/synexLogo';

/*
 * The marketing site's brand texture (Revision 1): the square grid of the
 * old site and of Synex's other marketing material (144px squares in
 * #f7f7f7, a few in #eee, #eee edges) behind every light hero, and the same
 * grid in night greys behind the night bands. Since 2026-10-13 the grid is
 * drawn inline, as SVG in the page (it was public/images/Pattern.svg, a
 * CSS background image), and the light heroes carry the old site's soft
 * orange cloud over it again. Decoration: hidden from assistive
 * technology, click-through, static and absolutely placed.
 *
 * Kept out of the shared primitives (Section, Typography ...): the heroes,
 * the error page and the night bands draw it, and MarketingLayout imports
 * it so it ships in the layout's chunk rather than a chunk of its own.
 */

/**
 * The grid's geometry: 144px squares, eight by six to a tile, and the seven
 * squares it fills, as [column, row] (the same cells as the article covers'
 * grid, Cover/template.js). A tile is 1152 by 864: the hairlines repeat
 * every 144px, the tile's seam included.
 */
export const GRID = Object.freeze({
    cell: 144,
    columns: 8,
    rows: 6,
    filled: Object.freeze([[3, 0], [7, 0], [1, 1], [6, 2], [0, 3], [3, 4], [5, 5]].map((cell) => Object.freeze(cell))),
});

const TILE_WIDTH = GRID.cell * GRID.columns;
const TILE_HEIGHT = GRID.cell * GRID.rows;

/** The light grid's colours, the old site's: squares, filled squares and the hairlines between them. */
export const GRID_COLOURS = Object.freeze({ cell: '#F7F7F7', filled: '#EEEEEE', line: '#EEEEEE' });

/** The same tile in night greys: squares in the night itself, filled squares #161616, hairlines #1f1f1f. */
export const NIGHT_GRID_COLOURS = Object.freeze({ cell: '#0D0D0D', filled: '#161616', line: '#1F1F1F' });

/**
 * The white laid over the grid, as the old site laid it (it used 0.6): the
 * grid stays quiet, and its darkest cell stays light enough for every hero
 * text at AA and the orange focus ring at 3:1 (at 0.6 the ring read 2.99:1).
 */
export const GRID_WASH = 0.65;

/** The grid fades out towards the hero's bottom edge, so the next section starts on the bare canvas. */
export const GRID_FADE = '[mask-image:linear-gradient(to_bottom,#000_45%,transparent)]';

/** Every hairline of a tile, half a pixel in so a 1px line covers whole pixels. */
const LINES = [
    ...Array.from({ length: GRID.rows }, (_, row) => `M0 ${row * GRID.cell + 0.5}H${TILE_WIDTH}`),
    ...Array.from({ length: GRID.columns }, (_, column) => `M${column * GRID.cell + 0.5} 0V${TILE_HEIGHT}`),
].join('');

/**
 * How far down a phone's grid reaches, in tile units: it is drawn through a
 * viewBox one tile wide, so the rect it fills has to outrun any hero (twenty
 * tiles down is 4,800px at 320px wide). It is two tiles wide for the same
 * reason; the box clips what it does not need.
 */
const PHONE_DEPTH = TILE_HEIGHT * 20;

/**
 * The grid, filled with `colours` and washed with `wash` of white. Under
 * 640px the tile spans the box's width, eight squares across, as the old
 * site drew it on a phone (at 144px a phone showed under three, and the
 * grid all but vanished), its hairlines still 1px; from 640px, the tile's
 * own size. Same colours and wash at either size, so the contrast holds.
 * It fills the nearest positioned box: the backdrops below, and the auth
 * pages' phone backdrop (AuthLayout).
 */
export function Grid({ colours, wash = 0 }) {
    const reactId = useId();
    const layers = [
        { key: 'phone', className: 'sm:hidden', viewBox: `0 0 ${TILE_WIDTH} ${TILE_HEIGHT}`, height: PHONE_DEPTH },
        { key: 'wide', className: 'hidden sm:block', viewBox: undefined, height: '100%' },
    ];

    return layers.map(({ key, className, viewBox, height }) => {
        const id = svgId(`mk-grid-${key}`, reactId);
        const width = viewBox ? TILE_WIDTH * 2 : '100%';

        return (
            <svg
                key={key}
                data-grid={key}
                aria-hidden="true"
                focusable="false"
                viewBox={viewBox}
                preserveAspectRatio="xMinYMin meet"
                className={`absolute inset-0 size-full ${className}`}
            >
                <defs>
                    <pattern id={id} width={TILE_WIDTH} height={TILE_HEIGHT} patternUnits="userSpaceOnUse">
                        <rect width={TILE_WIDTH} height={TILE_HEIGHT} fill={colours.cell} />
                        {GRID.filled.map(([column, row]) => (
                            <rect key={`${column},${row}`} x={column * GRID.cell} y={row * GRID.cell} width={GRID.cell} height={GRID.cell} fill={colours.filled} />
                        ))}
                        <path d={LINES} fill="none" stroke={colours.line} strokeWidth="1" vectorEffect={viewBox ? 'non-scaling-stroke' : undefined} />
                    </pattern>
                </defs>
                <rect width={width} height={height} fill={`url(#${id})`} />
                {wash > 0 && <rect width={width} height={height} fill="#FFFFFF" fillOpacity={wash} />}
            </svg>
        );
    });
}

/**
 * The cloud's outline, the old site's (its page headers drew the same one):
 * blurred, it reads as a soft, uneven cloud rather than a circle.
 */
const CLOUD_SHAPE =
    'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)';

/** How strong the cloud is at its heart, before the blur spreads it. */
export const CLOUD_OPACITY = 0.3;

/**
 * The old site's orange cloud (its heroes had one, 2026-10-13: "add some of
 * that back in, but don't overdo it"): a blurred shape in a gradient from
 * the secondary orange to the primary.
 *
 * It only ever shows inside `zone`, a box that clips it and fades it in at
 * its edges: the zone keeps it clear of every control and every grey or
 * orange line of text on the backdrop. The orange focus ring needs 3:1 on
 * what is under it and the grid's darkest cell leaves it nothing to spare
 * (3.01:1), so the faintest tint under a ring would fail it. `className`
 * places the cloud inside its zone. Behind a band of cards, where only the
 * gaps and the margins show it, it can be stronger (`opacity`, at most
 * BEHIND_CARDS_OPACITY).
 */
export function Cloud({ zone, className, opacity = CLOUD_OPACITY }) {
    return (
        <div aria-hidden="true" data-cloud="" className={`pointer-events-none absolute overflow-hidden ${zone}`}>
            <div className={`absolute transform-gpu blur-[64px] sm:blur-[90px] ${className}`}>
                <div
                    className="aspect-[1108/632] w-full bg-gradient-to-tr from-mk-night-orange to-mk-orange"
                    style={{ clipPath: CLOUD_SHAPE, opacity }}
                />
            </div>
        </div>
    );
}

/** A cloud behind cards: only the gaps between them and the margins beside them show it. */
export const BEHIND_CARDS_OPACITY = 0.5;

/**
 * A zone's sides for a cloud behind a band of cards: from the page's left
 * edge to its right, whatever box it is placed in, so the glow reaches the
 * margins beside the cards. The section clips what passes the viewport.
 */
export const FULL_WIDTH_ZONE = 'left-[calc((100%_-_100vw)/2)] right-[calc((100%_-_100vw)/2)]';

/**
 * The hero cloud's zone. From 1024px every hero keeps its controls and its
 * grey text in the left column (the widest, Resources' search field, ends
 * at 60% of the hero at 1024px), so the cloud takes the right 38%, faded in
 * from its left edge. Below 1024px the text runs the full width from 120px
 * down, so the cloud keeps to the strip above it, behind the header, and
 * fades out before the first line.
 */
const HERO_CLOUD_ZONE =
    'inset-x-0 top-0 h-[7.5rem] [mask-image:linear-gradient(to_bottom,#000_20%,transparent)] lg:bottom-0 lg:left-[62%] lg:h-auto lg:[mask-image:linear-gradient(to_right,transparent,#000_45%)]';

/** Where the hero's cloud sits in its zone: its heart over the top right corner. */
const HERO_CLOUD = 'right-[-8rem] top-[-11rem] w-[34rem] sm:w-[44rem] lg:right-[-9rem] lg:top-[-9rem] lg:w-[50rem]';

/**
 * Every light hero's backdrop: the grid, and the orange cloud over its top
 * right corner. Put it first inside the hero's `relative isolate` section;
 * it sits behind everything there.
 */
export function HeroBackdrop() {
    return (
        <div
            aria-hidden="true"
            data-hero-backdrop=""
            className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${GRID_FADE}`}
        >
            <Grid colours={GRID_COLOURS} wash={GRID_WASH} />
            <Cloud zone={HERO_CLOUD_ZONE} className={HERO_CLOUD} />
        </div>
    );
}

/** Faded in from the band's top and out towards its bottom, so it never meets the next section with an edge. */
const NIGHT_FADE = '[mask-image:linear-gradient(to_bottom,transparent,#000_12%,#000_88%,transparent)]';

/**
 * The night band's backdrop (2026-10-04: the bands that were orange-soft,
 * on Home, Platform, Contact, Resources and Partnerships, turned night): the
 * brand grid drawn in night greys, so the band carries the brand's pattern
 * as the light heroes do. Laid at 35% over the night (the user asked for a
 * quieter grid, 2026-10-05): its hairlines read about #131313 and its
 * filled cells about #101010. Its lightest colour is then darker than the
 * pattern's own filled cell (#161616), on which the night text was
 * measured: night-text 18.1:1, night-body 6.35:1, night-orange
 * 9.68:1, the orange focus ring 5.70:1 (on the night itself 19.4, 6.82,
 * 10.4 and 6.13). Put it first inside the band's `relative isolate`
 * section, its tone dark.
 */
export function NightBackdrop() {
    return (
        <div
            aria-hidden="true"
            data-night-backdrop=""
            className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden opacity-35 ${NIGHT_FADE}`}
        >
            <Grid colours={NIGHT_GRID_COLOURS} />
        </div>
    );
}
